import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject } from "rxjs";
import { Link } from "../../../shared/link/link";
import { LinkForm } from "../../../shared/link/link.form";
import {
  CrupdateLinkPayload,
  LinkService,
} from "../../../shared/link/link.service";
import { Toast } from "@common/core/ui/toast.service";
import { Messages } from "../../../messages.enum";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { CurrentUser } from "@common/auth/current-user";
import { LinkOptionsList } from "../types";
import { Settings } from "@common/core/config/settings.service";
import { finalize } from "rxjs/operators";
import * as urlRegex from "url-regex";
import autosize from "autosize";
import { Translations } from "@common/core/translations/translations.service";
import { RecaptchaService } from "@common/core/services/recaptcha.service";
import { BackendErrorResponse } from "@common/core/types/backend-error-response";
import { CrupdateLinkSelectValuesService } from "../crupdate-link-select-values.service";
import { LinkCreatedMessageComponent } from "./link-created-message/link-created-message.component";
import { CrupdateLinkFeature } from "../crupdate-link-feature";
import { DomainForm } from "src/app/shared/link/domain.form";
import { CustomDomain } from "@common/custom-domain/custom-domain";

export interface CrupdateLinkModalData {
  link?: Link;
  groups?: CrupdateLinkPayload["groups"];
  batch?: boolean;
  featuresToHide?: { [key in CrupdateLinkFeature]?: boolean };
  createMsg?: string;
}

@Component({
  selector: "crupdate-link-modal",
  templateUrl: "./crupdate-link-modal.component.html",
  styleUrls: ["./crupdate-link-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("bodyExpansion", [
      state("false", style({ height: "0px", visibility: "hidden" })),
      state("true", style({ height: "*", visibility: "visible" })),
      transition(
        "true <=> false",
        animate("225ms cubic-bezier(0.4,0.0,0.2,1)")
      ),
    ]),
  ],
  providers: [LinkForm],
})
export class CrupdateLinkModalComponent implements OnInit, AfterViewInit {
  @Input() linkOptions: LinkOptionsList;
  @ViewChild("textarea") textarea: ElementRef<HTMLTextAreaElement>;
  loading$ = new BehaviorSubject(false);
  updating$ = new BehaviorSubject(false);
  optionsVisible$ = new BehaviorSubject(false);
  linkOptions$ = new BehaviorSubject<LinkOptionsList>(null);
  getDomain = "Domain";

  defaultHost$ = new BehaviorSubject<string>(null);

  minLen: number = this.settings.get("links.min_len");
  maxLen: number = this.settings.get("links.max_len");

  constructor(
    private dialogRef: MatDialogRef<CrupdateLinkModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CrupdateLinkModalData,
    public linkForm: LinkForm,
    public domainForm: DomainForm,
    private links: LinkService,
    private toast: Toast,
    private selectValues: CrupdateLinkSelectValuesService,
    private currentUser: CurrentUser,
    public settings: Settings,
    private i18n: Translations,
    private recaptcha: RecaptchaService
  ) {
    if (data.link) {
      this.updating$.next(true);
      this.linkForm.patchValue(data.link);
    }
  }
  defaultDomain: any;
  ngOnChanges() {
    if (this.defaultDomain.length) {
      this.setDefaultHost();
    }
  }

  ngOnInit() {
    const payload = this.getPayload();

    console.log("defaultHost$", this.defaultHost$);
    this.selectValues.get().subscribe((response) => {
      this.linkOptions$.next(response);
      this.defaultDomain = response.domains;
      console.log("defaultDomain", this.defaultDomain);
    });
  }

  ngAfterViewInit() {
    autosize(this.textarea.nativeElement);
  }

  async createOrUpdate() {
    const payload = this.getPayload();
    if (this.urlInvalid(payload.long_url)) {
      return this.linkForm.setErrors({
        long_url: this.i18n.t("This url is invalid."),
      });
    } else if (!payload.long_url.startsWith("https")) {
      return this.linkForm.setErrors({
        long_url: this.i18n.t("This url is invalid."),
      });
    }

    this.loading$.next(true);

    if (!(await this.recaptcha.verify("link_creation"))) {
      return this.loading$.next(false);
    }

    const request = this.updating$.value
      ? this.links.update(this.data.link.id, payload)
      : this.links.create(payload);

    request.pipe(finalize(() => this.loading$.next(false))).subscribe(
      (response) => {
        this.updating$.value
          ? this.toast.open(Messages.LINK_UPDATE_SUCCESS)
          : this.toast.openComponent(LinkCreatedMessageComponent, {
              data: {
                link: response.link,
                createMsg: this.data.createMsg,
              },
              duration: 6000,
              panelClass: "link-created-snackbar",
            });
        this.close(response.link);
      },
      (e: BackendErrorResponse) => this.linkForm.setErrors(e.errors)
    );
  }

  async shortenBatchUrls() {
    const payload = this.getPayload();
    if (payload.long_urls.some((u) => this.urlInvalid(u))) {
      return this.linkForm.setErrors({
        long_urls: this.i18n.t("Some of the urls are not valid."),
      });
    }

    this.loading$.next(true);

    if (!(await this.recaptcha.verify("link_creation"))) {
      return this.loading$.next(false);
    }

    this.links
      .createBatch(payload)
      .pipe(finalize(() => this.loading$.next(false)))
      .subscribe(
        (response) => {
          this.toast.open(Messages.MULTIPLE_LINKS_CREATE_SUCCESS);
          this.close(response.links);
        },
        (e: BackendErrorResponse) => this.linkForm.setErrors(e.errors)
      );
  }

  close(data?: Link | Link[]) {
    this.dialogRef.close(data);
  }

  toggleOptions() {
    this.optionsVisible$.next(!this.optionsVisible$.value);
  }

  typeViewName() {
    const type = this.linkForm.formGroup.get("type").value;
    return type.split(":")[0];
  }

  typeViewDomainName() {
    const domain = this.domainForm.formGroup.get("domainselect").value;
    return domain;
  }
  private getPayload() {
    const payload = this.linkForm.value();
    if (this.data.groups) {
      payload.groups = { ...payload.groups, ...this.data.groups };
    }
    return payload;
  }

  private urlInvalid(url: string): boolean {
    return !urlRegex({ exact: true, strict: false }).test(url);
  }

  selectDomain(selectedDomain) {
    this.getDomain = selectedDomain;
    console.log("selectedDomain", selectedDomain);
  }

  private setDefaultHost() {
    debugger;
    const defaultHost = this.settings.get("custom_domains.default_host");
    if (defaultHost) {
      this.defaultHost$.next(
        this.defaultDomain.find((d) => d.host === defaultHost).host
      );
    } else {
      const baseUrl = this.settings
        .getBaseUrl()
        .replace(/\/$/, "")
        .replace(/(^\w+:|^)\/\//, "");
      this.defaultHost$.next(baseUrl);
    }
  }
}
